import { Heading } from '@chakra-ui/react';
import {
  ICustomFieldResSchema,
  IUsageEvent,
  IUsageTypeResSchema,
} from '@monetize/types/app';
import { UploadIcon } from '@monetize/ui/icons';
import {
  formatNumber,
  toDateShort,
  toDateTimeShort,
} from '@monetize/utils/core';
import { createColumnHelper } from '../../../components/Monetize/DataTableNew/appTable';
import { fromUnixTime } from 'date-fns/fromUnixTime';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useGetAccountById } from '../../../api/accountsService';
import {
  MBox,
  MButton,
  MFlex,
  MFormField,
  MPageContainer,
  MPageHeader,
  MPageLoader,
  MStatusIDBox,
  MText,
} from '../../../components/Monetize';
import DataTableActions from '../../../components/Monetize/DataTable/MDataTableActions';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import {
  SELECTION_COL,
  SELECTION_COL_KEY,
} from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { getColHeaderWithSize } from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import { DatePicker } from '../../../components/Monetize/DatePicker/DatePicker';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import {
  SubscriptionLookupPopover,
  UsageTypeLookupPopover,
} from '../../../components/Monetize/LookupPopover';
import MEmptyDataPlaceholder from '../../../components/Monetize/MEmptyDataPlaceholder';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { getAccountUsageRoute } from '../../../constants/routes';
import { useSubscriptionUsage } from '../../../hooks/AccountDetail/useSubscriptionUsage';
import { useUsageEventCustomFields } from '../../../hooks/useUsageEventCustomFields';
import { useFlags } from '../../../services/launchDarkly';
import { pluralize } from '../../../utils';
import { VoidUsageModal } from '../../Billing/Usage/VoidUsageModal';
import { ManualUsageForm } from '../Subscriptions/Usage/ManualUsageForm';
import {
  collectPropertyKeys,
  mergePropertyColumnKeys,
} from '../Subscriptions/Usage/usageProperties.utils';
import { getUsagePropertyColumns } from '../Subscriptions/Usage/usagePropertyColumns';
import { UploadUsageModal } from '../Subscriptions/Usage/usageUpload/UploadUsageModal';
import { UsageTableFilterActions } from './components/UsageTableFilterActions';
import { UsageTypeIDName } from './components/UsageTypeIDName';

const columnHelper = createColumnHelper<IUsageEvent>();
const pinnedLeftColumns = [SELECTION_COL_KEY];

function getColumns(
  usageTypes: IUsageTypeResSchema[],
  canUpdateUsage: boolean,
  isCanceled: boolean,
  onEdit: (record: IUsageEvent) => void,
  onVoid: (record: IUsageEvent) => void,
  propertyKeys: string[],
  customFieldsByKey: Record<string, ICustomFieldResSchema>,
) {
  const propertyColumns = getUsagePropertyColumns(
    columnHelper,
    propertyKeys,
    customFieldsByKey,
  );

  return [
    ...(canUpdateUsage
      ? [{ ...SELECTION_COL, enableColumnFilter: false }]
      : []),
    columnHelper.accessor('id', {
      ...getColHeaderWithSize('ID', 250),
      enableSorting: false,
    }),
    columnHelper.accessor('subscriptionId', {
      ...getColHeaderWithSize('Subscription ID', 250),
      enableSorting: false,
      cell: ({ row }) => (
        <SubscriptionLookupPopover
          value={row.original.subscriptionId}
          record={row.original}
        />
      ),
    }),
    columnHelper.accessor('usageTypeId', {
      ...getColHeaderWithSize('Usage Type ID', 250),
      enableSorting: false,
      cell: ({ row }) => (
        <UsageTypeLookupPopover value={row.original.usageTypeId} />
      ),
    }),
    columnHelper.display({
      id: 'timestamp',
      ...getColHeaderWithSize('Timestamp (UTC)', 250),
      enableSorting: false,
      cell: ({ row }) => (
        <MText>
          {toDateTimeShort(fromUnixTime(Number(row.original.timestamp)), 'UTC')}
        </MText>
      ),
    }),
    columnHelper.display({
      id: 'unitsConsumed',
      ...getColHeaderWithSize('Units', 250),
      enableSorting: false,
      cell: ({ row }) => {
        const usageType = usageTypes.find(
          ({ id }) => id === row.original.usageTypeId,
        );
        if (!usageType) {
          return null;
        }
        return (
          <MText>
            {formatNumber(row.original.unitsConsumed, {
              minimumFractionDigits: usageType.decimalPlaces || 0,
              maximumFractionDigits: usageType.decimalPlaces || 0,
            })}{' '}
            {usageType.unitName}
          </MText>
        );
      },
    }),
    columnHelper.display({
      id: 'usageType',
      ...getColHeaderWithSize('Usage Type', 160),
      enableSorting: false,
      cell: ({ row }) => {
        const usageType = usageTypes.find(
          ({ id }) => id === row.original.usageTypeId,
        );
        if (!usageType) {
          return null;
        }
        return <MText>{usageType.name}</MText>;
      },
    }),
    ...propertyColumns,
    columnHelper.display({
      id: 'actions',
      size: 32,
      enableSorting: false,
      cell: ({ row }) => {
        if (!canUpdateUsage) {
          return null;
        }
        return (
          <MFlex align="center" justify="flex-end">
            <DataTableActions
              actions={[
                {
                  title: 'Edit',
                  enabled: !isCanceled,
                  action: () => onEdit(row.original),
                },
                {
                  title: 'Void',
                  enabled: !isCanceled,
                  color: 'danger',
                  action: () => onVoid(row.original),
                },
              ]}
            />
          </MFlex>
        );
      },
    }),
  ];
}
export const UsageEventsPage: FC = () => {
  const params = useParams();

  const accountId = params.accountId ?? '';
  const subscriptionId = params.subscriptionId ?? '';
  const productId = params.productId ?? '';

  const { data: accountDetails } = useGetAccountById(accountId, {
    enabled: !!accountId,
  });

  const {
    formData,
    getUsageRecordsQuery,
    onOpenUsageForm,
    onCloseUsageForm,
    onOpenUsageUpload,
    onCloseUsageUpload,
    isUsageFormOpen,
    isManualUsageModalOpen,
    usageRecords,
    totalElements,
    filters,
    isCanceled,
    subscription,
    rate,
    isLoadingSubscription,
    activeUsageRecord,
    setActiveUsageRecord,
    subscriptionItem,
    canUpdateUsage,
    pager,
    earliestAllowedEditDate,
  } = useSubscriptionUsage({
    accountId,
    subscriptionId,
    productId,
  });

  const { control, formState, setValue, watch } = formData;
  const filterStartTime = watch('startTime');
  const {
    isLoading: isLoadingUsage,
    refetch,
    fetchNextPage,
  } = getUsageRecordsQuery;

  const [selectedCount, setSelectedCount] = useState(0);
  const [tableInstanceKey, setTableInstanceKey] = useState(0);
  const [activeVoidRecords, setActiveVoidRecords] = useState<
    IUsageEvent[] | null
  >(null);
  const tableRef = useRef<DataTableRef>(null);

  // Bumping the instance key remounts the table, which clears its own
  // selection state; the count is the local mirror that needs resetting.
  const resetSelection = useCallback(() => {
    setSelectedCount(0);
    setTableInstanceKey((value) => value + 1);
  }, []);

  const { enableUsageCustomFields } = useFlags();
  const { configuredKeys, customFieldsByKey } = useUsageEventCustomFields();
  const propertyKeys = useMemo(
    () =>
      enableUsageCustomFields
        ? mergePropertyColumnKeys(
            configuredKeys,
            collectPropertyKeys(usageRecords),
          )
        : [],
    [enableUsageCustomFields, configuredKeys, usageRecords],
  );
  const propertyKeysSignature = propertyKeys.join('|');

  const columns = useMemo(
    () =>
      getColumns(
        subscriptionItem?.product.usageTypes ?? [],
        canUpdateUsage,
        isCanceled,
        (record) => {
          setActiveUsageRecord(record);
          onOpenUsageForm();
        },
        (record) => {
          setActiveVoidRecords([record]);
        },
        propertyKeys,
        customFieldsByKey,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      subscriptionItem?.product.usageTypes,
      canUpdateUsage,
      isCanceled,
      propertyKeysSignature,
      customFieldsByKey,
    ],
  );

  // Handle the case where subscriptionItem might not be available yet
  if (!subscription || !subscriptionItem || !rate || isLoadingSubscription) {
    return <MPageLoader />;
  }

  const usageTypeIds = usageRecords.map((item) => item.usageTypeId);
  const uniqueUsageTypeIds = new Set(usageTypeIds);
  const hasSingleUsageTypeId = uniqueUsageTypeIds.size === 1;

  return (
    <MPageContainer maxHeight="100vh" enableAccountSearch>
      {isManualUsageModalOpen && (
        <UploadUsageModal
          accountId={accountId}
          subscriptionId={subscriptionId}
          onClose={onCloseUsageUpload}
        />
      )}

      {activeVoidRecords && (
        <VoidUsageModal
          usageEvents={activeVoidRecords}
          onClose={() => {
            setActiveVoidRecords(null);
            resetSelection();
            refetch();
          }}
        />
      )}

      <MPageHeader
        hasBackButton
        copyUrl
        id={hasSingleUsageTypeId ? usageTypeIds[0] : ''}
        parentLink={getAccountUsageRoute(accountId)}
        parentLinkTitle={`${accountDetails?.accountName ?? ''} - ${
          rate.name || ''
        }`}
        title={`${subscriptionItem.product.name} - Usage Events`}
        extraSubtitleParts={[
          <MStatusIDBox
            key="subscription-id"
            id={subscription.id}
            idCopyProps={{ tooltipMessage: 'Copy Subscription ID' }}
          />,
          <MStatusIDBox
            key="product-id"
            id={subscriptionItem.product.id}
            idCopyProps={{ tooltipMessage: 'Copy Product ID' }}
          />,
          <UsageTypeIDName
            key="usage-type-id"
            usageTypes={subscriptionItem.product.usageTypes || []}
            target="usageTypeId"
            singleIdCopyBoxProps={{ displayIcon: true }}
          />,
          <MText key={`subtitle-parts-1`}>
            Current Period: {toDateShort(subscription.usagePeriodStart || '')}{' '}
            &mdash; {toDateShort(subscription.usagePeriodEnd || '')}
          </MText>,
        ]}
      />
      <MFlex direction="column" w={'100%'} mb="5">
        <MFlex justifyContent="space-between" alignItems="center">
          <Heading size="md" fontSize={18} lineHeight={1}>
            Usage Events
          </Heading>
          <MFlex alignItems="center">
            <MFormField
              error={formState?.errors?.startTime}
              label="From"
              isHorizontal
              labelProps={{ mr: 2 }}
            >
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => (
                  <DatePicker includeTime onClose={refetch} {...field} />
                )}
              />
            </MFormField>
            <MFormField
              error={formState?.errors?.endTime}
              label="to"
              labelProps={{ fontWeight: 'normal', mx: 2 }}
              isHorizontal
            >
              <Controller
                name="endTime"
                control={control}
                render={({ field }) => (
                  <DatePicker includeTime onClose={refetch} {...field} />
                )}
              />
            </MFormField>
            {subscription.usagePeriodStart && (
              <UsageTableFilterActions
                currentUsagePeriodStart={subscription.usagePeriodStart}
                onChange={(startTime, endTime) => {
                  if (!setValue) {
                    return;
                  }
                  setValue('startTime', startTime);
                  setValue('endTime', endTime);
                }}
              />
            )}
            {canUpdateUsage && !isCanceled && selectedCount > 0 && (
              <MButton
                ml={2}
                variant="secondary"
                onClick={() =>
                  setActiveVoidRecords(
                    tableRef.current?.getSelectedRows<IUsageEvent>() ?? [],
                  )
                }
                type="button"
              >
                Void {selectedCount} {pluralize('Record', selectedCount)}
              </MButton>
            )}
            {canUpdateUsage && !isCanceled && (
              <MButton
                ml={2}
                mr={2}
                variant="secondary"
                onClick={() => onOpenUsageForm()}
              >
                New Usage
              </MButton>
            )}
            <RefreshTableButton refetch={refetch} />
            <MBox ml={2}>
              <ExportEntityButton
                entity="usage"
                applyInternalFiltersWithoutConfirmation
                internalFilters={filters}
                sortField={pager.sortField}
                sortOrder={pager.sortOrder}
                getFilename={() => `subscriptions-${accountId}.csv`}
              />
            </MBox>
            {canUpdateUsage && !isCanceled && (
              <DataTableActions
                actions={[
                  {
                    title: 'Import Usage',
                    icon: UploadIcon,
                    enabled: true,
                    action: () => onOpenUsageUpload(),
                  },
                ]}
              />
            )}
          </MFlex>
        </MFlex>
      </MFlex>
      {isUsageFormOpen && (
        <ManualUsageForm
          isOpen
          existingUsage={activeUsageRecord}
          onClose={() => {
            onCloseUsageForm();
            setActiveUsageRecord(undefined);
          }}
          subscriptionId={subscription.id}
          periodStartDate={subscription.periodStartDate}
          initialTimestamp={filterStartTime}
          usageTypes={subscriptionItem.product.usageTypes}
          accountId={accountId}
          earliestAllowedEditDate={earliestAllowedEditDate}
        />
      )}

      <DataTable
        ref={tableRef}
        key={tableInstanceKey}
        columns={columns}
        data={usageRecords}
        isLoading={isLoadingUsage}
        enablePagination={false}
        skipFilterValuePreprocessing
        pinnedLeftColumns={canUpdateUsage ? pinnedLeftColumns : undefined}
        onSelectedRowCountChange={canUpdateUsage ? setSelectedCount : undefined}
        emptyProps={{
          renderEmptyPlaceholder: () => (
            <MEmptyDataPlaceholder
              mainMessage="There aren't any usage events for this product."
              smallMessage={
                isCanceled
                  ? 'This subscription is canceled; no usage records can be added.'
                  : 'Add new usage event or change the date range.'
              }
              btnLabel={
                canUpdateUsage && !isCanceled ? 'Add New Usage' : undefined
              }
              onClick={onOpenUsageForm}
            />
          ),
        }}
        loadMoreProps={
          usageRecords.length > 0 ? { onLoadMore: fetchNextPage } : undefined
        }
        totalElements={totalElements}
      />
    </MPageContainer>
  );
};
